import { DocumentMetadataSanitizer, institutionManagementService } from "services";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { v4 as uuidv4 } from "uuid";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

class ApplicationService {
  private async getAuthInfo() {
    const session = await fetchAuthSession();
    const identityId = session.identityId;
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString();
    const userId = session.tokens?.idToken?.payload?.sub as string;
    const isAuthenticated = !!token;

    const domainRaw = idToken?.payload?.["custom:domain"];
    const domain = typeof domainRaw === "string" ? domainRaw : "";

    const userTypeRaw = idToken?.payload?.["custom:userType"];
    const userType = typeof userTypeRaw === "string" ? userTypeRaw : "";

    const email = idToken?.payload?.email as string;

    const isStudent = userType === "STUDENT";
    const isPartner = userType === "PARTNER";
    const isProvider = userType === "PROVIDER";
    const isEdubay = userType === "EDUBAY";

    const userData = {
      id: `${userId}::${idToken?.payload?.["cognito:username"] || ""}`,
      userType,
      domain,
      identityId,
      email,
    };

    return {
      idToken,
      token,
      userId,
      isAuthenticated,
      userData,
      domain,
      userType,
      isStudent,
      isPartner,
      isProvider,
      isEdubay,
    };
  }

  // Tested
  async getUserApplications(): Promise<any[]> {
    const { userData, token } = await this.getAuthInfo();

    if (userData.userType !== "STUDENT") return [];

    const { data, errors } = await client.models.Application.list({
      filter: {
        owner: { eq: userData.id },
      },
      authMode: "userPool",
      authToken: token,
    });

    if (errors) {
      console.error(`Error fetching applications:`, errors);
      throw new Error("Failed to fetch applications");
    }

    const applications =
      data?.filter((app) => app.userId === userData.id) || [];

    return applications;
  }

  // Tested
  async getApplicationDetails(
    institutionId: string,
    applicationId: string,
  ): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Application.get(
        {
          institutionId,
          applicationId,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error(
          `Error fetching application details for ID ${applicationId}:`,
          errors,
        );
        throw new Error("Failed to fetch application details");
      }

      return data;
    } catch (error) {
      console.error(
        `Error fetching application details for ID ${applicationId}:`,
        error,
      );
      throw error;
    }
  }

  async getApplicationsByCourse(
    institutionId: string,
    applicationId: string,
    courseId: string,
  ): Promise<any[]> {
    const { token } = await this.getAuthInfo();

    const { data, errors } = await client.models.Application.list({
      filter: {
        institutionId: { eq: institutionId },
        applicationId: { eq: applicationId },
      },
      authMode: "userPool",
      authToken: token,
    });

    if (errors) {
      console.error(`Error fetching applications:`, errors);
      throw new Error("Failed to fetch applications");
    }

    const applications = data?.filter((app) => app.courseId === courseId) || [];

    if (!applications) {
      throw new Error("Applications not found");
    }

    return applications;
  }

  async getApplicationsByPartner(): Promise<any[]> {
    const { userData, token } = await this.getAuthInfo();

    if (userData.userType !== "PARTNER") return [];

    const { data, errors } = await client.models.Application.list({
      filter: {
        partnerId: { eq: userData.domain },
      },
      authMode: "userPool",
      authToken: token,
    });

    if (errors) {
      console.error("Error fetching partner applications:", errors);
      throw new Error("Failed to fetch partner applications");
    }

    return data || [];
  }

  // Tested
  async getApplicationsByInstitution(
    institutionId: string,
    status?: string,
  ): Promise<any[]> {
    const { userData, token } = await this.getAuthInfo();

    if (
      userData.userType !== "EDUBAY" &&
      (userData.userType !== "PROVIDER" || userData.domain !== institutionId)
    ) {
      return [];
    }

    const { data: accessRecords } = await client.models.ApplicationAccess.list({
      filter: {
        entityId: { eq: institutionId },
      },
      authMode: "userPool",
      authToken: token,
    });

    if (!accessRecords || accessRecords.length === 0) {
      return [];
    }

    const applications = await Promise.all(
      accessRecords.map(async (access) => {
        const { data } = await client.models.Application.get(
          {
            institutionId: access.entityId,
            applicationId: access.applicationId,
          },
          { authMode: "userPool", authToken: token },
        );
        return data;
      }),
    );

    let filtered = applications.filter((app) => app !== null);

    if (status) {
      filtered = filtered.filter((app) => app.status === status);
    }

    return filtered;
  }

  // async deleteApplications(
  //   ids: string[]
  // ): Promise<GraphQLResult<DeleteApplicationResponse>[]> {
  //   return this.executeBatchGraphQL<DeleteApplicationResponse>(
  //     deleteApplication,
  //     ids,
  //     (id) => ({ input: { id } }),
  //     "Error deleting applications:"
  //   );
  // }

  // Tested
  async updateApplicationStatus(
    institutionId: string,
    applicationId: string,
    status: string,
  ): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const applicationStatus = status as
        | "DRAFT"
        | "SUBMITTED"
        | "UNDER_REVIEW"
        | "AWAITING_DOCUMENTS"
        | "CONDITIONAL_OFFER_ISSUED"
        | "UNCONDITIONAL_OFFER_ISSUED"
        | "ACCEPTED"
        | "REJECTED"
        | "WITHDRAWN";

      const data = await client.models.Application.update(
        {
          institutionId,
          applicationId,
          status: applicationStatus,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      return data;
    } catch (error) {
      console.error("Error updating application:", error);
      throw new Error("Failed to update application");
    }
  }

  // Tested
  async submitApplication(input: any): Promise<any> {
    try {
      const { token, userData } = await this.getAuthInfo();

      const data = await client.mutations.submitApplication(
        {
          input: {
            ...input,
            owner: userData.id,
            identityId: userData.identityId,
            email: userData.email,
          },
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (data.errors && data.errors.length > 0) {
        throw new Error(
          `Backend errors: ${data.errors.map((err: any) => err.message).join(", ")}`,
        );
      }

      if (!data.data) {
        throw new Error("Backend returned null data");
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async updateApplication(
    institutionId: string,
    applicationId: string,
    updateFields: Partial<Schema["Application"]["type"]>,
  ): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Application.update(
        {
          institutionId,
          applicationId,
          ...updateFields,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error updating application:", errors);
        throw new Error("Failed to update application");
      }

      return data;
    } catch (error) {
      console.error("Error updating application:", error);
      throw error;
    }
  }

  async linkToGenuineStudent(
    institutionId: string,
    applicationId: string,
    genuineStudentId: string,
  ): Promise<any> {
    try {
      return await this.updateApplication(institutionId, applicationId, {
        genuineStudentId,
      });
    } catch (error) {
      console.error(
        `Error linking application ${applicationId} to GS ${genuineStudentId}:`,
        error,
      );
      throw error;
    }
  }

  async linkToPreApplication(
    preApplicationId: string,
    applicationId: string,
    institutionId: string,
  ): Promise<any> {
    try {
      return await this.updateApplication(institutionId, applicationId, {
        preApplicationId,
      });
    } catch (error) {
      console.error(
        `Error linking application ${applicationId} to Pre Application ${preApplicationId}:`,
        error,
      );
      throw error;
    }
  }

  async assignAgentToApplication(
    institutionId: string,
    applicationId: string,
    agentId: string,
  ): Promise<any> {
    try {
      const institutionAgents =
        await institutionManagementService.getInstitutionAgents(institutionId);

      const isValidAgent = institutionAgents?.some(
        (agent: any) => agent.agentId === agentId,
      );

      if (!isValidAgent) {
        throw new Error("Agent is not associated with this institution");
      }

      return await this.updateApplication(institutionId, applicationId, {
        partnerId: agentId,
      });
    } catch (error) {
      console.error(
        `Error assigning agent ${agentId} to application ${applicationId}:`,
        error,
      );
      throw error;
    }
  }

  async getApplicationDocuments(id: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.DocumentLink.list({
        filter: {
          applicationId: {
            eq: id,
          },
        },
        authMode: "userPool",
        authToken: token,
      });

      if (errors) {
        console.error(
          `Error fetching application documents for ID ${id}:`,
          errors,
        );
        throw new Error("Failed to fetch application documents");
      }
      const sorted = (data || []).sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        return dateB - dateA;
      });

      const sanitizedDocs = sorted.map((doc) =>
        DocumentMetadataSanitizer.sanitizeDocumentForDisplay(doc),
      );
      return sanitizedDocs;
    } catch (error) {
      console.error(
        `Error fetching application documents for ID ${id}:`,
        error,
      );
      throw error;
    }
  }

  async getCourseDetails(id: string) {
    const { token } = await this.getAuthInfo();

    const { data, errors } = await client.models.Course.get(
      {
        courseId: id,
      },
      {
        authMode: "userPool",
        authToken: token,
      },
    );

    if (errors) {
      console.error("Error fetching course details:", errors);
      throw new Error("Failed to fetch course details");
    }

    return data;
  }

  // Application Notes
  async addNote(institutionId: string, applicationId: string, content: string) {
    try {
      const { token } = await this.getAuthInfo();
      const noteId = uuidv4();

      const result = await client.models.ApplicationNote.create(
        {
          noteId,
          institutionId: institutionId,
          applicationId: applicationId,
          content,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      return result;
    } catch (err) {
      console.error("Error adding note:", err);
      throw err;
    }
  }

  async removeNote(id: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const data = await client.models.ApplicationNote.delete(
        { noteId: id },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      return data;
    } catch (error) {
      console.error("Error removing note:", error);
      throw error;
    }
  }

  async getApplicationNotes(
    institutionId: string,
    applicationId: string,
  ): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.ApplicationNote.list({
        filter: {
          institutionId: {
            eq: institutionId,
          },
          applicationId: {
            eq: applicationId,
          },
        },
        authMode: "userPool",
        authToken: token,
      });

      if (errors) {
        console.error(
          `Error fetching application note for ID ${applicationId}:`,
          errors,
        );
        throw new Error("Failed to fetch application notes");
      }

      return data || [];
    } catch (error) {
      console.error(
        `Error fetching application notes for ID ${applicationId}:`,
        error,
      );
      throw error;
    }
  }
}
export const applicationService = new ApplicationService();
